import {FormArray, FormControl, FormGroup} from '@angular/forms';

import {map} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable} from 'rxjs';
import {switchMap} from 'rxjs/internal/operators/switchMap';

import {
    ApiError,
    ApiResponse,
    CountryModel,
    HttpStatusCodesEnum,
    IndividualNationalitiesService,
    IndividualNationalityInterface,
    IndividualExtendedModel,
    IndividualNationalityModel,
    IndividualNationalityInputModel,
} from '@spa/api';

import {DuplicatedValueValidatorKey, InArrayValidator} from '@spa/validators';

import {BaseControl, CountryAutocompleteControl, Helper} from '../../../../shared/classes';

import {NationalitiesFormArraySelector, NationalityControl} from '../../../../../assets/descriptors';

export class NationalitiesForm {
    private readonly individual: IndividualExtendedModel;
    private readonly countries: CountryModel[];
    private readonly nationalitiesService: IndividualNationalitiesService;

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    public readonly nationalitiesFormArraySelector: string;

    constructor(
        individual: IndividualExtendedModel,
        countries: CountryModel[],
        nationalitiesService: IndividualNationalitiesService
    ) {
        this.individual = individual;
        this.countries = !countries ? [] : countries;
        this.controls = [];
        this.nationalitiesService = nationalitiesService;
        this.nationalitiesFormArraySelector = NationalitiesFormArraySelector;

        this.controlsObservable = new BehaviorSubject([]);
    }

    private removeEmptyControls(emptyControlIndexes: number[], formArray: FormArray): void {
        emptyControlIndexes.sort((previousIndex: number, nextIndex: number) => {
            return nextIndex - previousIndex;
        }).map((index: number) => {
            formArray.removeAt(index);
            this.controls.splice(index, 1);
        });
    }

    private deleteRequestMap(
        nationalityForDelete: string,
        response: ApiResponse<ApiError[] | null>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<null>(response);
        }

        const indexOfNationality = this.individual.nationalities.indexOf(nationalityForDelete);

        this.individual.nationalities.splice(indexOfNationality, 1);

        return Helper.createSuccessResponse<boolean>(true);
    }

    private deleteRequestsMap(
        responses: ApiResponse<ApiError[] | boolean>[]
    ): Observable<ApiResponse<ApiError[] | boolean>> {
        if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
            return Helper.errorsHelper().createReducedErrorResponseObservable<boolean>(responses);
        }

        return this.createRegistrationRequests();
    }

    private createRegistrationRequests(): Observable<ApiResponse<ApiError[] | boolean>> {
        const formArray = this.form.controls[NationalitiesFormArraySelector] as FormArray;

        const requests = formArray.controls.map((control: FormControl) => {
            const model = new IndividualNationalityInputModel(this.individual.id, control.value);

            return this.nationalitiesService.register(model)
                .pipe(
                    map(this.registrationRequestMap.bind(this))
                );
        });

        return forkJoin(requests)
            .pipe(
                map(this.registrationRequestsMap)
            );
    }

    private registrationRequestMap(
        response: ApiResponse<ApiError[] | IndividualNationalityInterface>
    ): ApiResponse<ApiError[] | boolean> {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            return Helper.errorsHelper().createErrorResponse<IndividualNationalityInterface>(response);
        }

        const model = new IndividualNationalityModel(response.data as IndividualNationalityInterface);

        this.individual.nationalities.unshift(model.countryId);

        return Helper.createSuccessResponse<boolean>(true);
    }

    private registrationRequestsMap(
        responses: ApiResponse<ApiError[] | boolean>[]
    ): ApiResponse<ApiError[] | boolean> {
        if (Helper.errorsHelper().checkForErrorResponses<boolean>(responses)) {
            return Helper.errorsHelper().createReducedErrorResponse<boolean>(responses);
        }

        return Helper.createSuccessResponse<boolean>(true);
    }

    addNationality(): void {
        if (this.controls.length < 9) {
            const control = this.initControl();

            control.placeholder = control.placeholder + ' #' + this.controls.length;

            const formControl = new FormControl(control.value, control.validators);
            const formArray = this.form.get(NationalitiesFormArraySelector) as FormArray;

            this.controls.push(control);

            formArray.push(formControl);
        }
    }

    canAddNationality(): boolean {
        return this.controls && (this.controls.length < 9);
    }

    initControl(value?: string): BaseControl {
        const control = NationalityControl();

        if (value) {
            control.value = value;
        }

        const countriesIds = this.countries.map((country: CountryModel) => country.id);

        control.validators.push(InArrayValidator(countriesIds));

        (control as CountryAutocompleteControl).countries = this.countries;

        return control;
    }

    initForm(): void {
        this.form = new FormGroup({});
        this.controls = [];

        if (this.individual && this.individual.nationalities && this.individual.nationalities.length !== 0) {
            this.controls = this.individual.nationalities.map((nationality: string) => {
                return this.initControl(nationality);
            });
        } else {
            const control = this.initControl();

            this.controls.push(control);
        }

        const formArray = new FormArray([]);

        this.controls.map((control: BaseControl) => {
            const formControl = new FormControl(control.value, control.validators);

            formArray.push(formControl);
        });

        this.form.addControl(this.nationalitiesFormArraySelector, formArray);
        this.controlsObservable.next(this.controls);
    }

    getValues(): string[] {
        return this.form.get(NationalitiesFormArraySelector).value;
    }

    validateForm(messageFunction: (controlLabel?: string) => { [key: string]: string; }): boolean {
        const emptyFormControlIndexes = [];
        const formArray = (this.form.controls[this.nationalitiesFormArraySelector] as FormArray);
        const formControlValues = formArray.controls.map((control: FormControl) => control.value);
 
        const statuses = formArray.controls.map((formControl: FormControl, formControlIndex: number) => {
            if (!formControl.value && formControlIndex > 0) {
                emptyFormControlIndexes.push(formControlIndex);

                return true;
            } 

            if (formControl.invalid) {
                this.controls[formControlIndex].errors = Object.keys(formControl.errors).map((errorKey: string) => {
                    return messageFunction(this.controls[formControlIndex].placeholder)[errorKey];
                });

                return false;
            }

            const formControlValue = formControl.value;
            const matchedValueIndex = formControlValues.indexOf(formControlValue);

            if (formControlValue && (matchedValueIndex !== formControlIndex)) {
                const errorMessage =
                    messageFunction(this.controls[formControlIndex].placeholder)[DuplicatedValueValidatorKey];

                this.controls[formControlIndex].setError(errorMessage);

                return false;
            }

            return true;
        });

        if (emptyFormControlIndexes.length !== 0) {
            this.removeEmptyControls(emptyFormControlIndexes, formArray);
        }

        return statuses.indexOf(false) < 0;
    }

    createRequest(): Observable<ApiResponse<ApiError[] | boolean>> | null {
        if (this.form.dirty) {
            const requestStack = [];

            if (this.individual.nationalities.length !== 0) {
                const deleteRequests = this.individual.nationalities.map((nationality: string) => {
                    return this.nationalitiesService.deleteByIndividualId(this.individual.id, nationality)
                        .pipe(
                            map(this.deleteRequestMap.bind(this, nationality))
                        );
                });

                requestStack.push(...deleteRequests);
            }

            if (requestStack.length !== 0) {
                return forkJoin(requestStack)
                    .pipe(
                        switchMap(this.deleteRequestsMap.bind(this))
                    );
            }

            return this.createRegistrationRequests();
        }

        return null;
    }
}
